import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface HeroProps {
  onRequestLeads: () => void;
}

export default function Hero({ onRequestLeads: _onRequestLeads }: HeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    requestAnimationFrame(() => {
      el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    });
  }, []);

  const scrollToPreview = () => {
    document.getElementById("dashboard-preview")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2c5a9469ef69507fe5b87963f3cd1de6.jpg"
          alt="Solar neighborhood"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25"></div>
      </div>

      {/* Content */}
      <div ref={heroRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0"></span>
            <span className="text-white/90 text-sm font-medium">Live marketplace — new leads added daily</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight mb-5">
            Browse &amp; Buy{" "}
            <span className="text-green-400">Verified Solar Leads</span>
            {" "}On Demand
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-xl">
            Sign up, filter by state and score, and purchase exclusive leads instantly — no contracts, no minimums. Full contact details unlock the moment you pay.
          </p>

          {/* Bullets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {[
              "Live marketplace — buy only what you want",
              "Dual scoring: Lead Score + Solar Score",
              "Exclusive — 1 lead sold to 1 installer only",
              "Pipeline tracker built into your dashboard",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-green-400 text-lg"></i>
                </div>
                <span className="text-white/90 text-sm md:text-base font-medium">{item}</span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <a
              href="/signup"
              onClick={(e) => { e.preventDefault(); navigate("/signup"); }}
              className="bg-green-500 hover:bg-green-400 text-black font-bold px-8 py-4 rounded-full text-base transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              Create Free Account
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <button
              onClick={scrollToPreview}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-eye-line"></i>
              See Live Preview
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-5 text-white/60 text-sm">
            <div className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-green-400"></i>
              <span>No contracts</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-secure-payment-line text-green-400"></i>
              <span>Stripe-secured checkout</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-green-400"></i>
              <span>Limited slots per region</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/40 text-xs">Scroll</span>
        <i className="ri-arrow-down-line text-white/40 text-lg"></i>
      </div>
    </section>
  );
}
